import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import RequestFollowUpApi from "../../../infrastructure/api/requestFollowUp";
import JalaliDatePicker from "../shared/jalali-datepicker";
import RequestFollowUpTimeline from "./RequestFollowUpTimeline";

const parsePickerValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatFollowUpDateForApi = (value) => {
  if (!value) return null;
  const normalized = String(value).trim();
  if (!normalized) return null;
  return normalized.includes("T")
    ? normalized
    : normalized.replace(" ", "T");
};

const followUpTypeOptions = [
  { value: 1, label: "تماس تلفنی" },
  { value: 2, label: "واتساپ" },
  { value: 3, label: "پیامک" },
  { value: 4, label: "حضوری" },
  { value: 5, label: "یادداشت داخلی" },
];

const callResultOptions = [
  { value: 1, label: "پاسخ داد" },
  { value: 2, label: "پاسخ نداد" },
  { value: 3, label: "مشغول بود" },
  { value: 4, label: "شماره اشتباه بود" },
  { value: 5, label: "علاقه مند است" },
  { value: 6, label: "تمایل ندارد" },
  { value: 7, label: "نیاز به پیگیری دارد" },
  { value: 8, label: "آماده مرحله بعد" },
];

const RequestFollowUpDialog = ({
  open,
  onClose,
  source = "SolarentOperator",
  requestInfoId,
  onSubmitted,
}) => {
  const [followUpType, setFollowUpType] = useState(1);
  const [callResult, setCallResult] = useState("");
  const [note, setNote] = useState("");
  const [nextFollowUpDate, setNextFollowUpDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const needsFollowUp = Number(callResult) === 7;
  const nextFollowUpDateValue = useMemo(
    () => parsePickerValue(nextFollowUpDate),
    [nextFollowUpDate]
  );

  useEffect(() => {
    if (!open || !requestInfoId) return;
    RequestFollowUpApi.getByRequest(source, requestInfoId).then((res) => {
      const items = res?.data || [];
      setHistory(Array.isArray(items) ? items : []);
    });
  }, [open, requestInfoId, source]);

  const canSubmit = useMemo(() => {
    if (!requestInfoId) return false;
    if (needsFollowUp && !nextFollowUpDate) return false;
    return true;
  }, [needsFollowUp, nextFollowUpDate, requestInfoId]);

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.warning("برای نتیجه نیاز به پیگیری، تاریخ پیگیری بعدی الزامی است.");
      return;
    }
    setLoading(true);
    const payload = {
      RequestInfoId: Number(requestInfoId),
      FollowUpType: Number(followUpType),
      CallResult: callResult === "" ? null : Number(callResult),
      Note: note?.trim() || null,
      NextFollowUpDate: formatFollowUpDateForApi(nextFollowUpDate),
    };
    try {
      const res = await RequestFollowUpApi.create(source, payload);
      if (!res?.isSuccess) {
        toast.error(res?.message || "خطا در ثبت پیگیری");
        setLoading(false);
        return;
      }
      toast.success("پیگیری با موفقیت ثبت شد");
      setNote("");
      setCallResult("");
      setNextFollowUpDate("");
      const historyRes = await RequestFollowUpApi.getByRequest(source, requestInfoId);
      setHistory(historyRes?.data || []);
      onSubmitted?.();
    } catch (error) {
      toast.error("خطا در ثبت پیگیری");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ py: 1.25, fontSize: "1rem" }}>ثبت پیگیری</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="follow-up-type-label">نوع پیگیری</InputLabel>
            <Select
              labelId="follow-up-type-label"
              label="نوع پیگیری"
              value={followUpType}
              onChange={(e) => setFollowUpType(e.target.value)}
            >
              {followUpTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="call-result-label">نتیجه تماس/پیگیری</InputLabel>
            <Select
              labelId="call-result-label"
              label="نتیجه تماس/پیگیری"
              value={callResult}
              onChange={(e) => setCallResult(e.target.value)}
            >
              <MenuItem value="">ثبت نشده</MenuItem>
              {callResultOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <JalaliDatePicker
            label="تاریخ پیگیری بعدی"
            fullWidth
            size="small"
            value={nextFollowUpDateValue}
            disabled={loading}
            onChange={(date) => setNextFollowUpDate(date || "")}
            required={needsFollowUp}
            error={needsFollowUp && !nextFollowUpDate}
            helperText={
              needsFollowUp && !nextFollowUpDate ? "این فیلد الزامی است" : ""
            }
          />

          <TextField
            label="یادداشت"
            multiline
            minRows={2}
            size="small"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
          />

          <RequestFollowUpTimeline followUps={history} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose} color="inherit" size="small">
          بستن
        </Button>
        <Button disabled={loading} onClick={handleSubmit} variant="contained" size="small">
          ثبت پیگیری
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestFollowUpDialog;
